import i18n from "@microdoing/shared/i18n";
import { getIsPhoneNumberRequired } from "@microdoing/shared/services/client/selectors";
import { getProfilSettingsLoading } from "@microdoing/shared/services/profileSettings/selectors";
import { changeSignupInput } from "@microdoing/shared/services/signup/actions";
import {
  getSignupFirstName,
  getSignupLastName,
  getSignupRequesting,
} from "@microdoing/shared/services/signup/selectors";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import { useNavigation } from "@react-navigation/native";
import { Box, MainButton, MainText, RoundInput } from "app/components/common";
import HeaderScreen from "app/components/common/HeaderScreen";
import theme from "app/Theme/Theme";
import normalize from "app/utils/normalize";
import React, { useMemo, useRef } from "react";
import { Keyboard } from "react-native";
import { KeyboardAwareScrollView } from "react-native-keyboard-aware-scroll-view";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useDispatch, useSelector } from "react-redux";
import WebSetupName from "./WebSetupName";

const styles = {
  jouvie: {
    marginTop: theme.spacing.xs,
    position: "absolute",
    justifyContent: "flex-end",
    top: 0,
    right: 0,
    zIndex: 10,
  },
  scrollView: {
    flexGrow: 1,
    backgroundColor: theme.colors["neutral.white"],
    alignItems: "center",
    paddingBottom: theme.spacing.xm,
    width: "100%",
  },
  roundInput: {
    width: "100%",
    marginBottom: theme.spacing.m,
    borderRadius: 4,
    height: normalize(57),
  },
};

export interface ISetupNameProps {}

const SetupName = ({}: ISetupNameProps) => {
  const requesting = useSelector(getSignupRequesting);
  const firstName = useSelector(getSignupFirstName);
  const lastName = useSelector(getSignupLastName);
  const isPhoneNumberRequired = useSelector(getIsPhoneNumberRequired);
  const loading = useSelector(getProfilSettingsLoading);
  const insets = useSafeAreaInsets();

  const isButtonDisable = useMemo(() => {
    return !firstName || !lastName || requesting || loading;
  }, [firstName, lastName, requesting, loading]);

  const navigation = useNavigation();
  const dispatch = useDispatch();

  const onChangeFirstName = firstName => {
    dispatch(changeSignupInput({ firstName }));
  };

  const onChangeLastName = lastName => {
    dispatch(changeSignupInput({ lastName }));
  };

  const goNext = () => {
    Keyboard.dismiss();
    if (isPhoneNumberRequired) {
      return navigation.navigate("SignupPhoneNumber");
    }
    navigation.navigate("SignupSetupPicture");
  };

  const refInput = useRef();

  const renderBody = () => {
    return (
      <>
        <Box width="100%">
          <RoundInput
            testID="signup-firstName-input"
            containerPropStyle={styles.roundInput}
            onChangeText={onChangeFirstName}
            returnKeyType="next"
            placeholder={i18n.t("firstName")}
            value={firstName}
            autoFocus
            onSubmitEditing={() => refInput.current.focus()}
          />
        </Box>
        <Box width="100%">
          <RoundInput
            refInput={refInput}
            testID="signup-lastName-input"
            containerPropStyle={styles.roundInput}
            onChangeText={onChangeLastName}
            placeholder={i18n.t("lastName")}
            value={lastName}
            onSubmitEditing={goNext}
          />
        </Box>
      </>
    );
  };

  if (isWebDesktop) {
    return (
      <WebSetupName
        body={renderBody()}
        isButtonDisable={isButtonDisable}
        goNext={goNext}
      />
    );
  }

  return (
    <Box
      variant="screenView"
      backgroundColor="neutral.white"
      style={{ marginTop: insets.top }}
    >
      <HeaderScreen title={i18n.t("informations")} />
      <MainText
        style={{ marginTop: -theme.spacing.s }}
        marginBottom="m"
        variant="l6-medium"
        color="neutral.500"
      >
        {i18n.t("complete-profile")}
      </MainText>
      <Box flex={1}>
        <KeyboardAwareScrollView
          enableOnAndroid
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={styles.scrollView}
        >
          {renderBody()}
          <Box width="100%" alignSelf="center">
            <MainButton
              isLoading={requesting}
              isDisabled={isButtonDisable}
              onPress={goNext}
              text={i18n.t("next")}
            />
          </Box>
        </KeyboardAwareScrollView>
      </Box>
    </Box>
  );
};

export default SetupName;
